import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';

// Import sub-components
import ProgressSteps from './ProgressSteps';
import TeacherSelect from './TeacherSelect';
import DateSelect from './DateSelect';
import TimeSelect from './TimeSelect';
import { scheduleTranslations } from './scheduleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SchedulePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUserAuth();
  const { language } = useLanguage();
  const t = scheduleTranslations[language] || scheduleTranslations.en;

  // Step state
  const [step, setStep] = useState(1);
  
  // Data state
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Fetch teachers on mount
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/schedule/teachers`);
        setTeachers(response.data.teachers || []);
      } catch (error) {
        // Log to error tracking service in production
        // Teachers fetch failure is non-blocking - empty list will show message
        setTeachers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  // Fetch available slots when teacher and date are selected
  const fetchSlots = useCallback(async () => {
    if (!selectedTeacher || !selectedDate) return;
    
    setSlotsLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(
        `${BACKEND_URL}/api/schedule/teachers/${selectedTeacher.id}/slots?date=${dateStr}`
      );
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      // Slots fetch failure - show empty state
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [selectedTeacher, selectedDate]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Selection handlers
  const handleTeacherSelect = useCallback((teacher) => {
    setSelectedTeacher(teacher);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(2);
  }, []);

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(3);
  }, []);

  const handleSlotSelect = useCallback((slot) => {
    setSelectedSlot(slot);
  }, []);

  // Book class handler
  const handleBookClass = async () => {
    if (!isAuthenticated) {
      toast.error(t.loginRequired);
      navigate('/user-login', { state: { from: { pathname: '/schedule' } } });
      return;
    }

    if (!selectedTeacher || !selectedSlot) return;

    setBooking(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/schedule/book`,
        {
          teacher_id: selectedTeacher.id,
          scheduled_time: selectedSlot.datetime,
          duration_minutes: 50,
          class_type: 'regular'
        },
        { withCredentials: true }
      );
      toast.success(t.bookingSuccess);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.bookingError);
    } finally {
      setBooking(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-xl font-bold text-slate-900">English Passion</Link>
            {isAuthenticated && (
              <Link 
                to="/dashboard" 
                className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> {t.backToDashboard}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t.title}</h1>
          <p className="text-slate-600">{t.subtitle}</p>
        </motion.div>

        {/* Progress Steps */}
        <ProgressSteps currentStep={step} />

        {/* Step 1: Select Teacher */}
        {step === 1 && (
          <TeacherSelect 
            teachers={teachers} 
            t={t} 
            onSelect={handleTeacherSelect} 
          />
        )}

        {/* Step 2: Select Date */}
        {step === 2 && (
          <DateSelect
            t={t}
            language={language}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onBack={() => setStep(1)}
          />
        )}

        {/* Step 3: Select Time */}
        {step === 3 && (
          <TimeSelect
            t={t}
            language={language}
            selectedDate={selectedDate}
            selectedTeacher={selectedTeacher}
            selectedSlot={selectedSlot}
            availableSlots={availableSlots}
            slotsLoading={slotsLoading}
            booking={booking}
            onSlotSelect={handleSlotSelect}
            onBook={handleBookClass}
            onBack={() => setStep(2)}
          />
        )}
      </main>
    </div>
  );
};

export default SchedulePage;
